import { Box } from '@chakra-ui/react';
import { FormLabel, Text } from '@kula-ai/components';
import {
  evaluateFormula,
  referencedFields,
} from '@/pages/settings/ats/customFieldSettings/components/FieldValues/FormulaEditor/formulaEngine';

export interface CalculatedFieldValueProps {
  label: string;
  formula: string;
  // Whatever the form holds right now, keyed the way the config names fields.
  values: Record<string, unknown>;
  // Maps a formula token to the form field holding its value.
  fieldNameOf: (token: string) => string;
}

// Shown when the formula can't produce a value yet — a field it reads is still
// empty, or the formula itself is broken. Both are the author's problem, not
// the filler's, so the form stays quiet rather than showing an error.
export const NO_VALUE = '—';

const readTokens = (
  formula: string,
  values: Record<string, unknown>,
  fieldNameOf: (token: string) => string
) =>
  Object.fromEntries(
    referencedFields(formula).map((token) => [
      token,
      (values[fieldNameOf(token)] ?? null) as never,
    ])
  );

// The calculated value of a formula field, recomputed from the form's current
// values. Read-only: the person filling the form never types here.
export const CalculatedFieldValue = ({
  label,
  formula,
  values,
  fieldNameOf,
}: CalculatedFieldValueProps) => {
  let shown: string;
  try {
    const result = evaluateFormula(
      formula,
      readTokens(formula, values, fieldNameOf)
    );
    shown = result === null || result === '' ? NO_VALUE : String(result);
  } catch {
    shown = NO_VALUE;
  }

  return (
    <Box mt="3">
      <FormLabel className="mb-1 block">{label}</FormLabel>
      {/* kula Text drops data-* attributes, so the hook sits on the wrapper. */}
      <Box data-test-id="calculated-value">
        <Text variant="p">{shown}</Text>
      </Box>
    </Box>
  );
};
